import JSZip from "jszip";
import { useCallback, useEffect, useState } from "react";
import { trackEvent } from "../lib/analytics";
import {
	EC_PLATFORMS,
	type PlatformId,
	SNS_PLATFORMS,
} from "../lib/format/configs";
import {
	applyFreeTierRestriction,
	canvasToBlob,
	formatForPlatform,
} from "../lib/format/formatter";
import {
	FREE_DAILY_LIMIT_CONST,
	getRemainingFree,
	hasReachedFreeLimit,
	incrementUsage,
} from "../lib/freeTier/limits";
import { useInference } from "../lib/inference/useInference";
import { createBatchZip, generateZipFilename } from "../lib/zip/batchZip";
import { Progress } from "./ui/Progress";
// JSZip is used in downloadAll for single-item multi-platform ZIP
import { DropZone } from "./upload/DropZone";

interface ProcessedImage {
	file: File;
	original: string; // object URL
	foreground: HTMLCanvasElement;
	selectedPlatforms: PlatformId[];
}

interface ProcessingPanelProps {
	isPro?: boolean;
}

export function ProcessingPanel({ isPro = false }: ProcessingPanelProps) {
	const { state, removeBackground } = useInference();
	const [queue, setQueue] = useState<ProcessedImage[]>([]);
	const [processingFile, setProcessingFile] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const handleFiles = useCallback(
		async (files: File[]) => {
			if (state.status !== "ready") return;

			for (const file of files) {
				// 有料ユーザーは制限なし
				if (!isPro && hasReachedFreeLimit()) {
					setError(
						`無料枠の上限（${FREE_DAILY_LIMIT_CONST}枚/日）に達しました。有料プランにアップグレードしてください。`,
					);
					break;
				}

				setProcessingFile(file.name);
				setError(null);

				try {
					const { foreground } = await removeBackground(file);
					// 無料ユーザーのみ使用回数をカウント
					if (!isPro) incrementUsage();
					trackEvent("use_feature", { feature_name: "background_removal" });
					const originalUrl = URL.createObjectURL(file);

					setQueue((prev) => [
						...prev,
						{
							file,
							original: originalUrl,
							foreground,
							selectedPlatforms: ["transparent", "amazon"],
						},
					]);
				} catch (err) {
					setError(
						err instanceof Error ? err.message : "処理中にエラーが発生しました",
					);
				} finally {
					setProcessingFile(null);
				}
			}
		},
		[state.status, removeBackground, isPro],
	);

	const togglePlatform = (imgIdx: number, platform: PlatformId) => {
		setQueue((prev) =>
			prev.map((item, i) => {
				if (i !== imgIdx) return item;
				const platforms = item.selectedPlatforms.includes(platform)
					? item.selectedPlatforms.filter((p) => p !== platform)
					: [...item.selectedPlatforms, platform];
				return { ...item, selectedPlatforms: platforms };
			}),
		);
	};

	const downloadSingle = async (
		item: ProcessedImage,
		platformId: PlatformId,
	) => {
		let canvas = formatForPlatform(item.foreground, platformId);
		// 無料ユーザーのみ解像度制限+透かしを適用
		if (!isPro) canvas = applyFreeTierRestriction(canvas);
		const transparent = platformId === "transparent";
		const blob = await canvasToBlob(canvas, transparent);
		const ext = transparent ? "png" : "jpg";
		const fileName = `${item.file.name.replace(/\.[^.]+$/, "")}_${platformId}.${ext}`;
		triggerDownload(blob, fileName);
	};

	const downloadAll = async (item: ProcessedImage) => {
		if (item.selectedPlatforms.length === 0) return;

		if (item.selectedPlatforms.length === 1) {
			await downloadSingle(item, item.selectedPlatforms[0]);
			return;
		}

		const zip = new JSZip();
		for (const platformId of item.selectedPlatforms) {
			let canvas = formatForPlatform(item.foreground, platformId);
			if (!isPro) canvas = applyFreeTierRestriction(canvas);
			const transparent = platformId === "transparent";
			const blob = await canvasToBlob(canvas, transparent);
			const ext = transparent ? "png" : "jpg";
			zip.file(`${platformId}.${ext}`, blob);
		}

		const zipBlob = await zip.generateAsync({ type: "blob" });
		const baseName = item.file.name.replace(/\.[^.]+$/, "");
		triggerDownload(zipBlob, `${baseName}_formatted.zip`);
	};

	const downloadBatchAll = async () => {
		if (queue.length < 2) return;
		const items: { blob: Blob; filename: string }[] = [];
		for (const item of queue) {
			for (const platformId of item.selectedPlatforms) {
				let canvas = formatForPlatform(item.foreground, platformId);
				if (!isPro) canvas = applyFreeTierRestriction(canvas);
				const transparent = platformId === "transparent";
				const blob = await canvasToBlob(canvas, transparent);
				const ext = transparent ? "png" : "jpg";
				const baseName = item.file.name.replace(/\.[^.]+$/, "");
				items.push({ blob, filename: `${baseName}_${platformId}.${ext}` });
			}
		}
		if (items.length === 0) return;
		const zipBlob = await createBatchZip(items);
		triggerDownload(zipBlob, generateZipFilename());
	};

	const removeItem = (idx: number) => {
		setQueue((prev) => {
			URL.revokeObjectURL(prev[idx].original);
			return prev.filter((_, i) => i !== idx);
		});
	};

	const [remaining, setRemaining] = useState(FREE_DAILY_LIMIT_CONST);

	useEffect(() => {
		setRemaining(getRemainingFree());
	}, []);

	return (
		<div className="max-w-5xl mx-auto px-4 py-8">
			{/* Model loading state */}
			{state.status === "loading-model" && (
				<div className="mb-6">
					<Progress value={state.progress} label="AIモデルを読み込み中..." />
					<p className="text-xs text-gray-500 mt-1 text-center">
						初回のみダウンロードが必要です（約43MB）。次回以降はキャッシュから即時起動します。
					</p>
				</div>
			)}

			{state.status === "error" && (
				<div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
					エラー: {state.message}
				</div>
			)}

			{/* Free tier badge / Pro badge */}
			<div className="mb-4 flex items-center justify-between">
				{isPro ? (
					<div className="text-sm text-purple-700 font-medium flex items-center gap-1.5">
						<span className="text-purple-500">✨</span> プロプラン —
						無制限・フル解像度
					</div>
				) : (
					<div className="text-sm text-gray-500">
						本日の残り無料枠:{" "}
						<span className="font-semibold text-gray-800">
							{remaining}/{FREE_DAILY_LIMIT_CONST}枚
						</span>
						<span className="text-xs text-gray-400 ml-1">
							（低解像度・透かしあり）
						</span>
					</div>
				)}
				{!isPro && (
					<a
						href="#upgrade"
						className="inline-flex items-center text-sm bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 min-h-[48px] rounded-full hover:opacity-90 transition"
					>
						¥500/月 でフル解像度 →
					</a>
				)}
			</div>

			{/* Drop zone */}
			<DropZone
				onFiles={handleFiles}
				disabled={state.status !== "ready" || !!processingFile}
			/>

			{/* Processing indicator */}
			{processingFile && (
				<div className="mt-4 flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
					<svg
						className="animate-spin h-5 w-5 text-blue-600"
						fill="none"
						viewBox="0 0 24 24"
					>
						<circle
							className="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							strokeWidth="4"
						/>
						<path
							className="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						/>
					</svg>
					<span className="text-blue-700">背景除去中: {processingFile}</span>
				</div>
			)}

			{/* Error */}
			{error && (
				<div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
					{error}
				</div>
			)}

			{/* Batch download button (2枚以上の時に表示) */}
			{queue.length >= 2 && (
				<div className="mt-6 text-center">
					<button
						onClick={downloadBatchAll}
						className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition text-sm"
					>
						<span>📦</span>
						{queue.length}枚すべてをZIPで一括ダウンロード
					</button>
				</div>
			)}

			{/* Results */}
			{queue.length > 0 && (
				<div className="mt-8 space-y-8">
					{queue.map((item, idx) => (
						<ResultCard
							key={idx}
							item={item}
							onTogglePlatform={(p) => togglePlatform(idx, p)}
							onDownloadAll={() => downloadAll(item)}
							onDownloadSingle={(p) => downloadSingle(item, p)}
							onRemove={() => removeItem(idx)}
						/>
					))}
				</div>
			)}
		</div>
	);
}

interface ResultCardProps {
	item: ProcessedImage;
	onTogglePlatform: (p: PlatformId) => void;
	onDownloadAll: () => void;
	onDownloadSingle: (p: PlatformId) => void;
	onRemove: () => void;
}

function ResultCard({
	item,
	onTogglePlatform,
	onDownloadAll,
	onDownloadSingle,
	onRemove,
}: ResultCardProps) {
	const previewUrl = item.foreground.toDataURL("image/png");

	return (
		<div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
			{/* Header */}
			<div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
				<span className="text-sm font-medium text-gray-700 truncate max-w-xs">
					{item.file.name}
				</span>
				<button
					onClick={onRemove}
					className="text-gray-400 hover:text-gray-600"
				>
					<svg
						className="w-5 h-5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				</button>
			</div>

			<div className="p-4">
				{/* Preview comparison */}
				<div className="grid grid-cols-2 gap-4 mb-4">
					<div>
						<p className="text-xs text-gray-500 mb-1 text-center">元画像</p>
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img
							src={item.original}
							alt="original"
							className="w-full h-48 object-contain bg-gray-100 rounded"
						/>
					</div>
					<div>
						<p className="text-xs text-gray-500 mb-1 text-center">背景除去後</p>
						{/* eslint-disable-next-line @next/next/no-img-element */}
						<img
							src={previewUrl}
							alt="result"
							className="w-full h-48 object-contain rounded"
							style={{
								background:
									"repeating-conic-gradient(#e5e7eb 0% 25%, #fff 0% 50%) 0 0 / 20px 20px",
							}}
						/>
					</div>
				</div>

				{/* Platform selection */}
				<div className="mb-4">
					<p className="text-xs font-medium text-gray-600 mb-2">ECモール</p>
					<div className="flex flex-wrap gap-2 mb-3">
						{EC_PLATFORMS.map((p) => (
							<PlatformChip
								key={p}
								platformId={p}
								selected={item.selectedPlatforms.includes(p)}
								onToggle={() => onTogglePlatform(p)}
								onDownload={() => onDownloadSingle(p)}
							/>
						))}
					</div>
					<p className="text-xs font-medium text-gray-600 mb-2">SNS</p>
					<div className="flex flex-wrap gap-2 mb-3">
						{SNS_PLATFORMS.map((p) => (
							<PlatformChip
								key={p}
								platformId={p}
								selected={item.selectedPlatforms.includes(p)}
								onToggle={() => onTogglePlatform(p)}
								onDownload={() => onDownloadSingle(p)}
							/>
						))}
					</div>
					<p className="text-xs font-medium text-gray-600 mb-2">その他</p>
					<div className="flex flex-wrap gap-2">
						<PlatformChip
							platformId="transparent"
							selected={item.selectedPlatforms.includes("transparent")}
							onToggle={() => onTogglePlatform("transparent")}
							onDownload={() => onDownloadSingle("transparent")}
						/>
					</div>
				</div>

				{/* Download buttons */}
				<button
					onClick={onDownloadAll}
					disabled={item.selectedPlatforms.length === 0}
					className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
				>
					{item.selectedPlatforms.length <= 1
						? "ダウンロード"
						: `${item.selectedPlatforms.length}フォーマットをZIPでダウンロード`}
				</button>
			</div>
		</div>
	);
}

import { PLATFORM_CONFIGS } from "../lib/format/configs";

function PlatformChip({
	platformId,
	selected,
	onToggle,
	onDownload,
}: {
	platformId: PlatformId;
	selected: boolean;
	onToggle: () => void;
	onDownload: () => void;
}) {
	const config = PLATFORM_CONFIGS[platformId];
	return (
		<div
			className={`flex items-center rounded-full border text-xs overflow-hidden transition-all
      ${selected ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-white"}`}
		>
			<button
				onClick={onToggle}
				className="px-3 py-1.5 hover:bg-blue-100 transition"
			>
				{selected && <span className="text-blue-600 mr-1">✓</span>}
				{config.label}
				{config.width > 0 && (
					<span className="text-gray-400 ml-1">
						{config.width}×{config.height}
					</span>
				)}
			</button>
			<button
				onClick={onDownload}
				className="px-2 py-1.5 border-l border-gray-200 hover:bg-gray-100 transition text-gray-500"
				title="個別ダウンロード"
			>
				↓
			</button>
		</div>
	);
}

function triggerDownload(blob: Blob, fileName: string) {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = fileName;
	a.click();
	URL.revokeObjectURL(url);
}
